"use client";

import { useEffect, useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import { useAuthorStore } from "@/stores/authorStore";
import { API_CONFIG } from "@/config/api";

interface ModalEditAuthorProps {
  isOpen: boolean;
  onClose: () => void;
  authorId: string | null;
  onComplete: () => void;
}

export default function ModalEditAuthor({ isOpen, onClose, authorId, onComplete }: ModalEditAuthorProps) {
  const { fetchAuthorById, updateAuthor, isUpdating } = useAuthorStore();
  const [form, setForm] = useState<{ name?: string; info?: string }>({});
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (isOpen && authorId) {
        setLoading(true);
        const data = await fetchAuthorById(authorId);
        if (data) {
          setForm({ name: data?.name || "", info: data?.info || "" });
          setPreview(data?.image ? `${API_CONFIG.API_FILE_URL}${data?.image}` : null);
        }
        setLoading(false);
      }
    };
    load();
  }, [isOpen, authorId, fetchAuthorById]);

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!authorId) return;
    let payload: FormData | { name?: string; info?: string } = form;
    if (file) {
      const fd = new FormData();
      if (form.name !== undefined) fd.append('name', form.name);
      if (form.info !== undefined) fd.append('info', form.info);
      fd.append('image', file);
      payload = fd;
    }
    const ok = await updateAuthor(authorId, payload as any);
    if (ok) {
      onComplete();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
        <form onSubmit={submit}>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Chỉnh sửa tác giả</h2>
            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tên tác giả</label>
                <input
                  value={form.name || ""}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hình ảnh</label>
                <div
                  className={`rounded-lg ${preview ? 'p-3' : 'p-8'} ${preview ? 'inline-flex' : 'block'} items-center justify-center text-center cursor-pointer transition-colors ${isDragging
                    ? "border-2 border-dashed border-black bg-white"
                    : "border border-dashed border-gray-300 hover:bg-gray-50"
                    }`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) {
                      setFile(f);
                      setPreview(URL.createObjectURL(f));
                    }
                  }}
                  onClick={() => (document.getElementById('author-image-edit-input') as HTMLInputElement)?.click()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setFile(f);
                      setPreview(f ? URL.createObjectURL(f) : preview);
                    }}
                    className="hidden"
                    id="author-image-edit-input"
                  />
                  {!preview && (
                    <div className="flex flex-col items-center justify-center gap-2 text-gray-500">
                      <ImageIcon className="h-8 w-8" />
                      <div className="text-sm">Kéo & thả hoặc bấm để thêm ảnh poster</div>
                      <div className="text-xs text-gray-400">(Chỉ nhận file ảnh, tối đa 5MB)</div>
                    </div>
                  )}
                  {preview && (
                    <div className="relative inline-block">
                      <img src={preview} alt="preview" className="h-48 w-auto rounded-xl border object-contain" />
                      <button
                        type="button"
                        aria-label="remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          setPreview(null);
                        }}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white text-gray-700 shadow flex items-center justify-center hover:bg-gray-100 border"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thông tin</label>
                <textarea
                  value={form.info || ""}
                  onChange={(e) => setForm((p) => ({ ...p, info: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 min-h-[90px]"
                />
              </div>
            </div>
          )}

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button type="submit" disabled={isUpdating} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Lưu thay đổi</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Hủy</button>
          </div>
        </form>
      </div>
    </div>
  );
}


